import type { ReactNode } from 'react';
import { useCallback, useEffect, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import FeatureGates from '@atlaskit/feature-gate-js-client';
import { Analytics } from '@trello/atlassian-analytics';
import { updateAllSharedStateFeatureGatesAndExperiments } from '@trello/feature-gate-client';
import { intl } from '@trello/i18n';
import { getScreenFromUrl } from '@trello/marketing-screens';
import { Button } from '@trello/nachos/button';
import { InformationIcon } from '@trello/nachos/icons/information';
import { Spinner } from '@trello/nachos/spinner';
import { token } from '@trello/theme';

import { useDocumentTitle } from 'app/src/components/DocumentTitle';
import { Error } from 'app/src/components/Error';
import { useMemberLabsPageAuthorization } from './useMemberLabsPageAuthorization';

import styles from './MemberLabsPage.less';

export const MemberLabsPage = () => {
  useDocumentTitle(
    intl.formatMessage({
      id: 'templates.member_labs_page.labs-page-title',
      defaultMessage: 'Labs',
      description: 'Title for member labs page',
    }),
  );

  const resetOverridesHandler = useCallback(() => {
    FeatureGates.clearAllOverrides();
    updateAllSharedStateFeatureGatesAndExperiments();

    Analytics.sendClickedButtonEvent({
      buttonName: 'resetFeatureFlagOverridesButton',
      source: getScreenFromUrl(),
    });
  }, []);

  useEffect(() => {
    Analytics.sendScreenEvent({
      name: 'memberLabsScreen',
    });
  }, []);

  const labFeatures = useMemo(() => {
    const features: ReactNode[] = [];

    // Add LabFeature components to the `features` array here

    return features;
  }, []);

  const authorization = useMemberLabsPageAuthorization();
  if (authorization.loading) {
    return <Spinner />;
  }

  if (!authorization.canView) {
    return <Error errorType="notFound" />;
  }

  return (
    <section className={styles.pageContainer}>
      <header className={styles.pageHeaderContainer}>
        <h1 className={styles.pageHeader}>
          <FormattedMessage
            id="templates.member_labs_page.labs-page-title"
            defaultMessage="Labs"
            description="Title for member labs page"
          />
        </h1>
        <div className={styles.pageHeaderActions}>
          <Button appearance="danger" onClick={resetOverridesHandler}>
            <FormattedMessage
              id="templates.member_labs_page.reset-feature-flags-to-defaults"
              defaultMessage="Reset defaults"
              description="Button to reset feature flags to default values"
            />
          </Button>
        </div>
      </header>
      <p className={styles.pageDescription}>
        <FormattedMessage
          id="templates.member_labs_page.labs-page-description"
          defaultMessage="Help us test these new features before they are released to all users. Remember, these features aren't completely finished and you may experience changes or bugs."
          description="Description for member labs page"
        />
      </p>
      {labFeatures.length ? (
        labFeatures
      ) : (
        <div className={styles.pageInfoContainer}>
          <InformationIcon
            size="medium"
            color={token('color.icon.accent.blue', '#1D7AFC')}
            dangerous_className={styles.pageInfoIcon}
          />
          <FormattedMessage
            id="templates.member_labs_page.labs-page-no-features-available-to-test"
            defaultMessage="There are no features that are currently available to test."
            description="Message for member labs page when no features are available to test"
            tagName="p"
          />
        </div>
      )}
    </section>
  );
};
